/**
 * ProfilePhoto.jsx — Profile photo frame for StarTip creator cards.
 *
 * Visual container: 470×625 px proportion (aspect-ratio: 470/625 ≈ 3:4).
 * The image stored in S3 is 400×400 px (square). This component renders it
 * inside the 470×625 frame using object-fit:cover + object-position:center,
 * cropping the square to fill the portrait rectangle — centred, no distortion.
 *
 * Usage:
 *   <ProfilePhoto
 *     src={creator.photo_url}
 *     alt={creator.name}
 *     privacyMode={creator.avatar_privacy_mode}   // "normal" | "pixelated"
 *     className="rounded-xl border-2 border-border"  // applied to outer container
 *     imgClassName="group-hover:scale-105 transition-transform duration-500"
 *     fallback={<div className="..." />}
 *   />
 *
 * Layout contract:
 *   • Outer div: aspect-[470/625], overflow-hidden, relative
 *     (width is controlled by the parent — scales responsively)
 *   • <img>: absolute inset-0, w-full h-full, object-cover, object-position:center
 *   • privacyMode="pixelated" applies blur+desaturate (same as UserAvatar)
 *
 * CSS note:
 *   470 ÷ 625 = 0.752 — portrait frame, slightly wider than 9:16 (0.5625).
 *   A 400×400 source on a 470×625 frame: CSS crops ~16% top+bottom evenly,
 *   keeping the face/subject centred.
 */

const API_BASE = typeof window !== "undefined" ? window.location.origin : (process.env.REACT_APP_BACKEND_URL || "");

export function ProfilePhoto({
  src,
  alt = "",
  privacyMode = "normal",
  className = "",        // Tailwind classes for the outer container (shape, border…)
  imgClassName = "",     // extra classes for <img> (hover transitions, etc.)
  fallback = null,
}) {
  const isBlurred   = privacyMode === "pixelated";
  const resolvedSrc = src
    ? src.startsWith("http") ? src : `${API_BASE}${src}`
    : null;

  return (
    <div
      className={`relative overflow-hidden aspect-[470/625] ${className}`}
      data-profile-photo
    >
      {resolvedSrc ? (
        <img
          src={resolvedSrc}
          alt={alt}
          className={`absolute inset-0 w-full h-full object-cover object-center ${imgClassName}`}
          style={isBlurred ? { filter: "blur(10px) saturate(0.35)" } : undefined}
          loading="lazy"
          decoding="async"
        />
      ) : (
        fallback
      )}
    </div>
  );
}
